import { Layers } from 'lucide-react'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { matchAny, placement } from '../model'
import type { ClusterPlacement, NodePlacement, World } from '../model'
import { ClusterCard } from './ClusterCard'
import { PlacedNodeCard } from './PlacedNodeCard'

// 클러스터 — 어떤 노드가 묶여 있고 얼마나 쓰는가. 클러스터 카드 → 노드 카드 한 계층이 한 화면이고, VM 은 노드의 할당량과 대수로만 보인다.
// 할당과 대수는 노드가 싣는 서버의 값(`allocated`·`vm_count`)이다 — 이 화면은 VM 을 읽지 않는다.
// 클러스터 격자는 1280px 부터 두 열이고, 한 열일 때 카드 폭은 456px 까지다 — 두 열이 되는 순간의 열 폭과 같아 창을 드래그해도 카드가 튀지 않는다.
// 클러스터가 없는 노드는 `클러스터 미연결` 로 — 사라지는 노드가 없다. VM 하나하나는 VM 화면의 것이다.

export default function Clusters({ world, query, loading }: { world: World; query: string; loading: boolean }) {
  const all = placement(world)
  const p = query ? narrow(all, query) : all
  const nodeCount = world.nodes.length
  const vmCount = world.nodes.reduce((n, r) => n + r.node.vm_count, 0)
  const nothing = p.clusters.length === 0 && p.unclustered.length === 0

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<Layers className="size-5" />}
        title="클러스터"
        facts={loading ? '불러오는 중' : `클러스터 ${world.clusters.length}개 · 노드 ${nodeCount}대 · VM ${vmCount}대`}
      />

      {nothing ? (
        <div className="pt-6">
          <Empty>{query ? '검색 결과 없음' : '클러스터 없음'}</Empty>
        </div>
      ) : (
        <>
          {p.clusters.length > 0 ? (
            <div className="grid grid-cols-[minmax(0,456px)] items-start gap-4 pt-6 xl:grid-cols-2">
              {p.clusters.map((c) => (
                <ClusterCard key={c.cluster.id} placed={c} />
              ))}
            </div>
          ) : null}
          {p.unclustered.length > 0 ? (
            <Section title="클러스터 미연결">
              <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(210px,100%),1fr))]">
                {p.unclustered.map((n) => (
                  <PlacedNodeCard key={n.node.row.node.id} placed={n} />
                ))}
              </div>
            </Section>
          ) : null}
        </>
      )}
    </>
  )
}

/** 검색은 이름을 좁힌다 — 노드·클러스터. 이름이 맞는 클러스터는 통째로 남고, 이름이 맞는 노드가 있는 클러스터는 그 노드만 남는다. */
function narrow(p: ReturnType<typeof placement>, query: string): ReturnType<typeof placement> {
  const nodeHit = (n: NodePlacement) => matchAny([n.node.row.name], query)
  const narrowCluster = (c: ClusterPlacement): ClusterPlacement | null => {
    if (matchAny([c.cluster.name], query)) return c
    const nodes = c.nodes.filter(nodeHit)
    return nodes.length > 0 ? { ...c, nodes } : null
  }
  return {
    clusters: p.clusters.flatMap((c) => {
      const kept = narrowCluster(c)
      return kept ? [kept] : []
    }),
    unclustered: p.unclustered.filter(nodeHit),
  }
}
